import { forEach as _forEach, filter as _filter } from 'lodash';
import moment from 'moment';

import { DateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils';

const { dateFormats, dateTransformer } = DateCurrencyFromatter.dateCurrencyFromatter;

const today = dateTransformer(new Date(), dateFormats.calendarFormat);
const CURRENT_MONTH_DATE = moment();
const NEXT_MONTH_DATE = moment().add(1, 'M');
const PREV_MONTH_DATE = moment().subtract(1, 'M');

const AddonsTypes = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  RENEWABLE: 'renewable',
};
const AddOnsDurationEnum = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
};

const filterAddonsByPrevCurntNextMonth = addons => {
  return _filter(addons, addon => {
    return (
      moment(addon.startingDate).isBetween(PREV_MONTH_DATE, NEXT_MONTH_DATE) &&
      moment(addon.expirationDate).isBetween(PREV_MONTH_DATE, NEXT_MONTH_DATE)
    );
  });
};
const getMonthViewDates = () => {
  // we need to show only three monthes on timeline , prev month , current month and next month
  const allDates = {
    [PREV_MONTH_DATE]: {},
    [CURRENT_MONTH_DATE]: {},
    [NEXT_MONTH_DATE]: {},
  };
  let monthsList = {};
  let index = -1;
  for (const _date in allDates) {
    const dateMonth = dateTransformer(_date, dateFormats.monthNumber);
    const dateYear = dateTransformer(_date, dateFormats.year);

    // for monthview we need to represent month in four bars on timeline , bar for day 1 and for 07 and for 14 and for 21 (business requirement)
    const dateMonthString = `${dateYear}-${dateMonth}-01`;
    const firstWeekEnd = `${dateYear}-${dateMonth}-07`;
    const secondWeekEnd = `${dateYear}-${dateMonth}-14`;
    const thirdWeekEnd = `${dateYear}-${dateMonth}-21`;

    // only day one (01) has main bar so that date can be shown above the bar otherwise don't show the date above the bar
    monthsList[dateMonthString] = { isMainBar: true, index: ++index };
    monthsList[firstWeekEnd] = { isMainBar: false, index: ++index };
    monthsList[secondWeekEnd] = { isMainBar: false, index: ++index };
    monthsList[thirdWeekEnd] = { isMainBar: false, index: ++index };

    monthsList[today] = { isMainBar: false, index: ++index };
    monthsList = sortDates(monthsList);
  }
  return monthsList;
};
const getDatesFromAddons = addons => {
  // for dailyview we will get dates from addons and mark them with mainBar so date label can be shown above the timeline bar
  const allDates = {};
  _forEach(addons, element => {
    const startingDate = dateTransformer(element.startingDate, dateFormats.calendarFormat);
    const expirationDate = dateTransformer(element.expirationDate, dateFormats.calendarFormat);
    if (element.isAutoRenewable) {
      const renewableExpirationDateDate = getAddonExpirationDate(
        element.duration,
        element.expirationDate,
      );
      allDates[renewableExpirationDateDate] = { isMainBar: true };
    }
    allDates[startingDate] = { isMainBar: true };

    // (business requirement): there should be a bar separates between addon start date bar and addon end date bar ... we make mainbar = false so we don't need to show its date
    allDates[dateTransformer(moment(startingDate).add(1, 'days'), dateFormats.calendarFormat)] = {
      isMainBar: false,
    };

    allDates[expirationDate] = { isMainBar: true };
  });
  return allDates;
};
const getDatesFromCurrentPlan = currentPlan => {
  const allDates = {};
  allDates[dateTransformer(currentPlan.startDate, dateFormats.calendarFormat)] = {
    isMainBar: true,
  };
  allDates[dateTransformer(currentPlan.endDate, dateFormats.calendarFormat)] = {
    isMainBar: true,
  };
  if (currentPlan.isRenewable) {
    const nextCurrentPlanEndDate = dateTransformer(
      moment(currentPlan.endDate).add(1, 'M'),
      dateFormats.calendarFormat,
    );
    const previousCurrentPlanEndDate = dateTransformer(
      moment(currentPlan.startDate).subtract(1, 'M'),
      dateFormats.calendarFormat,
    );

    allDates[nextCurrentPlanEndDate] = { isMainBar: true };
    allDates[previousCurrentPlanEndDate] = { isMainBar: true };
  }
  return allDates;
};
/**
 * timeline daily view dates
 * @param  {Array} addons [addons list]
 * @param  {Object} currentPlan [The current plan details object]
 * @return {Array} [daily view dates generated from addons and currentPlan dates  ->   ex: {2021-04-12:{}}
 */
const getDailyViewDates = (addons, currentPlan) => {
  let allDates = {};
  // we only need addons that in range with previous,current,next month
  const filteredAddons = filterAddonsByPrevCurntNextMonth(addons);
  const sortedAddons = sortDates(filteredAddons);

  allDates = {
    ...getDatesFromAddons(sortedAddons),
    ...getDatesFromCurrentPlan(currentPlan),
  };

  return sortDates(allDates);
};
/**
 * Get final dates list to be rendered on the the timeline playground based on the type of the view.
 * @param  {Array} data [addons list]
 * @param  {Object} currentPlan [The current plan details object]
 * @param  {Boolean} isMonthView [To render nonth or days view]
 * @return {Array} [Final deates list to be rendered on the timeline playground, ex: [2020-10-01, 2020-11-15 ....]]
 */

const getDaysArray = (data, isMonthView, currentPlan) => {
  return isMonthView ? getMonthViewDates() : getDailyViewDates(data, currentPlan);
};

/**
 * get the bar number of a given date.
 * @param  {Object} dates [List of timeline dates] {"2020-04-21":{}}
 * @param  {String} date [string date]
 * @return {Number} [returns the index of the bar of a given date]
 */
const getDateBarNumber = (dates, date) => {
  const formattedDate = dateTransformer(date.date, dateFormats.calendarFormat);
  return dates[formattedDate]
    ? dates[formattedDate].index
    : getApproximatelyDateBarNumber(dates, date);
};

/**
 * get approximately the bar number of a given date for month view.
 * @param  {Object} dates [List of timeline dates] {"2020-04-21":{}}
 * @param  {String} date [string date]
 * @return {Number} [returns the index of the bar of a given date]
 */
const getApproximatelyDateBarNumber = (dates, date) => {
  const formattedDate = dateTransformer(date.date, dateFormats.calendarFormat);
  const dateDay = dateTransformer(formattedDate, dateFormats.dayNumber);
  const dateMonth = dateTransformer(formattedDate, dateFormats.monthNumber);
  const dateYear = dateTransformer(formattedDate, dateFormats.year);

  const index = dates[formattedDate] ? dates[formattedDate].index : -1;

  if (index !== -1) {
    return index;
  }
  const day = parseInt(dateDay, 10);
  let _index = -1;
  // incase we didn't find given date in dates object then try to put addon on one of nearst bar to its starting,ending Date (ex:bar 01 or bar 07 , bar 14 , bar 21)
  if (day >= 1 && day <= 7) {
    const stringDate = `${dateYear}-${dateMonth}-01`;
    _index = dates[stringDate] ? dates[stringDate].index : 0;
  }
  if (day > 7 && day <= 14) {
    const stringDate = `${dateYear}-${dateMonth}-07`;
    _index = dates[stringDate] ? dates[stringDate].index : 0;
  }
  if (day > 14 && day <= 21) {
    const stringDate = `${dateYear}-${dateMonth}-14`;
    _index = dates[stringDate] ? dates[stringDate].index : 0;
  }
  if (day > 21) {
    const stringDate = `${dateYear}-${dateMonth}-21`;

    _index = dates[stringDate] ? dates[stringDate].index : 0;
  }

  return _index;
};

/**
 * Sort array of dates.
 * @param   {Object} dates [List of timeline dates] {"2020-04-21":{}}
 * @return {Array} [returns sorted dates with index created]
 */
const sortDates = dates => {
  return Object.entries(dates)
    .sort(
      ([a], [b]) =>
        dateTransformer(a, dateFormats.yearMonthDayConcatinated) -
        dateTransformer(b, dateFormats.yearMonthDayConcatinated),
    )
    .reduce((r, [k, v], index) => ({ ...r, [k]: { ...v, index } }), {});
};
/**
 * Validate current plan object.
 * @param  {Object} currentPlan [current plan object]
 * @return {Boolean} [returns boolean a true or false]
 */
const isValidCurrentPlan = currentPlan => {
  const { name, startDate, endDate, currency } = currentPlan;
  return name && startDate && endDate && currency && isDate(startDate) && isDate(endDate);
};

/**
 *  Check a given date is a valid one.
 * @param  {String} date [string date]
 * @return {Boolean} [returns boolean a true or false]
 */
const isDate = date => {
  return new Date(date) != 'Invalid Date' && !isNaN(new Date(date));
};
/**
 *  Check a given date is expired (before today).
 * @param  {String} date [string date]
 * @return {Boolean} [returns boolean a true or false]
 */
const isExpiredDate = date => {
  return moment(today).isAfter(date);
};

/**
 *  Check a expiration date for a given addon.
 * @param  {AddOnsDurationEnum} duration [string date]
 * @param  {String} date [string date]
 * @return {String} [returns expiration date]
 */
const getAddonExpirationDate = (duration, startDate) => {
  const _startDate = moment(startDate);
  switch (duration) {
    case AddOnsDurationEnum.DAY:
      return dateTransformer(moment(_startDate).add(1, 'days'), dateFormats.calendarFormat);
    case AddOnsDurationEnum.WEEK:
      return dateTransformer(moment(_startDate).add(7, 'days'), dateFormats.calendarFormat);
    // case month or any other case
    default:
      return dateTransformer(moment(_startDate).add(1, 'M'), dateFormats.calendarFormat);
  }
};

export {
  today,
  AddonsTypes,
  isDate,
  isValidCurrentPlan,
  getApproximatelyDateBarNumber,
  getAddonExpirationDate,
  getDaysArray,
  isExpiredDate,
  AddOnsDurationEnum,
  getDateBarNumber,
  sortDates,
};
